import { apiConfig, http } from "@api/base";
import { documentApi } from "@api/documents";
import { getSearchTotal } from "@api/utils";
import { invenioConfig } from "@config";
import { loanApi } from "@api/loans";

const circulationStatsURL = "/circulation/stats/";
const mostLoanedURL = `${circulationStatsURL}most-loaned`;
const apiMostLoanedURL = `${apiConfig.baseURL}${mostLoanedURL}`;

const getMostLoanedDocumentsParams = (
  fromDate,
  toDate,
  size = null,
  format = null
) => {
  const params = {};
  if (fromDate) {
    params.from_date = fromDate;
  }
  if (toDate) {
    params.to_date = toDate;
  }
  if (size) {
    params.size = size;
  }
  if (format) {
    const formatArgName = invenioConfig.APP.REST_MIME_TYPE_QUERY_ARG_NAME;
    params[formatArgName] = format;
  }
  return params;
};

const getMostLoanedDocuments = async (fromDate, toDate) => {
  const params = {
    params: getMostLoanedDocumentsParams(fromDate, toDate),
  };
  const response = await http.get(mostLoanedURL, params);
  response.data.total = getSearchTotal(response.data.hits);
  response.data.hits = response.data.hits.hits.map((hit) =>
    documentApi.serializer.fromJSON(hit)
  );
  return response;
};

const getOverdueDocuments = async (fromDate, toDate) => {
  try {
    const query = loanApi
      .query()
      .overdue()
      .withState(invenioConfig.CIRCULATION.loanActiveStates);

    if (fromDate) {
      query.withStartDate({ fromDate });
    }
    if (toDate) {
      query.withStartDate({ toDate });
    }

    const queryString = query.qs();
    const response = await loanApi.list(queryString);
    const documentMap = new Map();

    if (response.data && response.data.hits) {
      response.data.hits.forEach((loan) => {
        if (loan.metadata && loan.metadata.document) {
          const docPid = loan.metadata.document.pid;
          const patron = loan.metadata.patron || null;
          if (!documentMap.has(docPid)) {
            documentMap.set(docPid, {
              id: loan.id,
              metadata: {
                pid: docPid,
                title: loan.metadata.document.title || "Unknown Title",
                edition: loan.metadata.document.edition,
                publication_year: loan.metadata.document.publication_year,
                loan_count: 1,
                extension_count: loan.metadata.extension || 0,
                end_date: loan.metadata.end_date,
                patron: patron,
              },
            });
          } else {
            const doc = documentMap.get(docPid);
            doc.metadata.loan_count += 1;
            doc.metadata.extension_count += loan.metadata.extension || 0;
            if (!doc.metadata.patron) {
              doc.metadata.patron = patron;
            }
          }
        }
      });
    }

    const transformedDocuments = Array.from(documentMap.values());

    return {
      data: {
        hits: transformedDocuments,
        total: transformedDocuments.length,
      },
    };
  } catch (error) {
    console.warn(
      "Using mock data for overdue documents until API endpoint is available",
      error
    );

    return {
      data: {
        hits: [
          {
            id: 1,
            metadata: {
              pid: "doc-1",
              title: "Overdue Book 1",
              edition: "1st Edition",
              publication_year: "2020",
              loan_count: 5,
              extension_count: 2,
              end_date: "2023-05-01",
            },
          },
          {
            id: 2,
            metadata: {
              pid: "doc-2",
              title: "Overdue Book 2",
              edition: "2nd Edition",
              publication_year: "2018",
              loan_count: 3,
              extension_count: 1,
              end_date: "2023-05-10",
            },
          },
          {
            id: 3,
            metadata: {
              pid: "doc-3",
              title: "Overdue Book 3",
              edition: "Special Edition",
              publication_year: "2021",
              loan_count: 7,
              extension_count: 0,
              end_date: "2023-04-25",
            },
          },
        ],
        total: 3,
      },
    };
  }
};

const getTopThreeBorrowedBooks = async () => {
  const params = {
    params: getMostLoanedDocumentsParams(null, null, 3),
  };
  const response = await http.get(mostLoanedURL, params);
  response.data.total = getSearchTotal(response.data.hits);
  response.data.hits = response.data.hits.hits.map((hit) =>
    documentApi.serializer.fromJSON(hit)
  );
  return response;
};

const getTopBorrowers = async (fromDate, toDate) => {
  try {
    let query = loanApi
      .query()
      .withState(invenioConfig.CIRCULATION.loanCompletedStates)
      .sortByNewest();

    if (fromDate && toDate) {
      query = query.withStartDate({ fromDate, toDate });
    } else if (fromDate) {
      query = query.withStartDate({ fromDate });
    } else if (toDate) {
      query = query.withStartDate({ toDate });
    }

    const pageSize = 1000;
    query = query.withSize(pageSize);

    const allLoans = [];
    let currentPage = 1;
    let hasMorePages = true;

    while (hasMorePages) {
      const paginatedQuery = query.withPage(currentPage);
      const queryString = paginatedQuery.qs();
      const response = await loanApi.list(queryString);

      if (
        response.data &&
        response.data.hits &&
        response.data.hits.length > 0
      ) {
        allLoans.push(...response.data.hits);
        const totalHits = response.data.total || 0;
        const currentTotal = currentPage * pageSize;
        hasMorePages = currentTotal < totalHits;
        currentPage++;
      } else {
        hasMorePages = false;
      }
    }

    const patronMap = new Map();

    allLoans.forEach((loan) => {
      const patron = loan.metadata?.patron;
      if (patron?.id) {
        const patronId = patron.id;
        if (!patronMap.has(patronId)) {
          patronMap.set(patronId, {
            id: patronId,
            name: patron.name || "Unknown Patron",
            email: patron.email || null,
            loan_count: 1,
          });
        } else {
          const patronData = patronMap.get(patronId);
          patronData.loan_count += 1;
        }
      }
    });

    const topBorrowers = Array.from(patronMap.values())
      .sort((a, b) => b.loan_count - a.loan_count)
      .slice(0, 50);

    return {
      data: {
        hits: topBorrowers,
        total: topBorrowers.length,
        totalLoansProcessed: allLoans.length,
      },
    };
  } catch (error) {
    throw new Error(`Failed to fetch top borrowers: ${error.message}`);
  }
};

const getPickBorrowingStats = async (fromDate, toDate) => {
  const query = loanApi
    .query()
    .withState(invenioConfig.CIRCULATION.loanCompletedStates)
    .sortByRequestStartDateAsc()
    .withSize(1000);

  if (fromDate || toDate) {
    query.withStartDate({ fromDate, toDate });
  }

  const response = await loanApi.list(query.qs());
  const loans = response.data.hits || [];

  const stats = {
    byHour: Array(24).fill(0),
    byDay: { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 },
    byWeek: {},
    byMonth: {},
  };

  const getWeekNumber = (date) => {
    const d = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    );
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  };

  loans.forEach((loan) => {
    const meta = loan.metadata;

    const raw =
      meta?.transaction_date ||
      meta?.timestamp ||
      meta?.created ||
      meta?.updated ||
      loan?.created ||
      loan?.updated ||
      meta?.request_start_date;

    if (!raw) {
      console.warn("Missing usable date in loan:", loan);
      return;
    }

    const isoString = raw.replace(" ", "T");
    const date = new Date(isoString);

    if (isNaN(date)) {
      console.warn(`Invalid date format: ${raw}`);
      return;
    }

    const hour = date.getHours();
    const day = date.toLocaleString("en-US", { weekday: "short" });
    const week = `${date.getFullYear()}-W${getWeekNumber(date)}`;
    const month = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}`;

    stats.byHour[hour]++;
    stats.byDay[day]++;
    stats.byWeek[week] = (stats.byWeek[week] || 0) + 1;
    stats.byMonth[month] = (stats.byMonth[month] || 0) + 1;
  });

  return { data: stats };
};

export const circulationStatsApi = {
  getMostLoanedDocumentsParams,
  getMostLoanedDocuments,
  getTopThreeBorrowedBooks,
  mostLoanedUrl: apiMostLoanedURL,
  getOverdueDocuments,
  getTopBorrowers,
  getPickBorrowingStats,
};
